import z from 'zod';

import {
  HostCommissionType,
  ListingAddOnLimitType,
  MediaType,
  MediaLinkSchema,
  ListingPictureKind,
  PromotedListingStatus,
  PositionSchema,
  ShapeSchema,
  NumberPattern,
  PaymentMethods,
  BookingAvailabilityModeValue,
  IntegrationMetaSchema,
  ShouldValidateComplimentaryBookingSchema,
  AvailabilityRuleSchema,
} from '../../..';
import { AddOnStatus } from '../../add-on/add-on';

type ArrayToRecord<T extends readonly string[]> = {
  [K in T[number]]: K;
};

export const DefaultImageResolutions = ['360', '720', '1080'] as const;
export const DefaultImageResolutionKeys = [
  ...DefaultImageResolutions.map((r) => `${r}w` as const),
  'original' as const,
] as const;

export const DefaultPriceTierSchemaV3 = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
  price: z.number(),
});

export const PictureSchemaV3 = z.object({
  id: z.string().uuid(),
  uri: z.object(Object.fromEntries(DefaultImageResolutionKeys.map((resolution) => [resolution, z.string()]))),
});
export const PictureResolutions = Object.fromEntries(DefaultImageResolutionKeys.map((key) => [key, key])) as ArrayToRecord<
  typeof DefaultImageResolutionKeys
>;

export const SessionSchemaV3 = z.object({
  startTime: z.string(),
  duration: z.number(),
  supportedParticipantsCount: z.number(),
  priceTiers: z.array(DefaultPriceTierSchemaV3),
});

export const TaxSchemaV3 = z.object({
  id: z.string().uuid(),
  name: z.string().optional().nullable(),
  percentage: z.number().optional().nullable(),
  amount: z.number().optional().nullable(),
  includedInPrice: z.boolean(),
  isHostTax: z.boolean().optional().nullable(),
  order: z.number().int().min(0).optional().nullable(),
  showInAdvertisedPrice: z.boolean().optional().nullable(),
  appliesTo: z.array(z.string().uuid()).optional().nullable(),
});

export type TaxV3 = z.infer<typeof TaxSchemaV3>;

export const AddOnSchemaV3 = z.object({
  listingAddOnId: z.string().uuid(),
  addOnId: z.string().uuid(),
  label: z.string(),
  price: z.number(),
  quantity: z.number().optional().nullable(),
  description: z.string().optional().nullable(),
  status: z.nativeEnum(AddOnStatus).nullable(),
  hostCommission: z.number().optional().nullable(),
  hostCommissionType: z.nativeEnum(HostCommissionType).optional().nullable(),
  picture: PictureSchemaV3.optional().nullable(),
  limit: z.number().optional().nullable(),
  limitType: z.nativeEnum(ListingAddOnLimitType).optional().nullable(),
  onlineBookingCutoffHours: z.number().optional().nullable(),
  order: z.number().optional().nullable(),
  taxes: z.array(TaxSchemaV3),
});

export type AddOnV3 = z.infer<typeof AddOnSchemaV3>;

export const CategorySchemaV3 = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  name: z.string(),
});

export const FirstAvailableDateSchemaV3 = z.object({
  scheduledDate: z.string().optional().nullable(),
  sessions: z.array(SessionSchemaV3),
});

export const HostedByBaseSchemaV3 = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  description: z.string().optional().nullable(),
});

export const HostedBySchemaV3 = HostedByBaseSchemaV3.extend({
  profilePicture: PictureSchemaV3.optional().nullable(),
});

export enum ListingsV3SortBy {
  CATEGORY = 'category',
  CREATED_AT = 'createdAt',
  STATUS = 'status',
  TITLE = 'title',
  LISTING_ORDER = 'listingOrder', // requires an additional join; only supported by select queries
}

export const LocationSchemaV3 = z.object({
  id: z.string().uuid(),
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  timezone: z.string(),
  additionalInfo: z.string().optional().nullable(),
});

export const MediasSchemaV3 = z.object({
  id: z.string().uuid(),
  kind: z.nativeEnum(ListingPictureKind),
  type: z.nativeEnum(MediaType),
  links: z.array(MediaLinkSchema),
  altText: z.string().nullable().optional(),
});

export type MediaV3 = z.infer<typeof MediasSchemaV3>;

export const PointsEligibleSchemaV3 = z.object({
  org: z.string(),
  payEligible: z.boolean(),
  earnEligible: z.boolean(),
});

export const PromotedListingSchemaV3 = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  promoterAgreementId: z.string().uuid(),
  promoterBrandId: z.string().uuid(),
  promoterBrandName: z.string(),
  providerBrandId: z.string().uuid(),
  providerBrandName: z.string(),
  status: z.nativeEnum(PromotedListingStatus),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ResourceGroupBaseSchemaV3 = z.object({
  id: z.string().uuid(),
  resourceGroupCollectionId: z.string().uuid(),
  title: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  integrationMeta: z.array(IntegrationMetaSchema).optional().nullable(),
});

export const ResourceGroupExperienceSchemaV3 = z.object({
  bookingAvailabilityMode: z.nativeEnum(BookingAvailabilityModeValue).nullable(),
  currency: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  displayCapacity: z.number().optional().nullable(),
  healthAndSafety: z.string().optional().nullable(),
  id: z.string().uuid(),
  headline: z.string().optional().nullable(),
  maximumAllowedToBook: z.number().optional().nullable(),
  maxParticipantCount: z.number().optional().nullable(),
  minimumRequiredToBook: z.number().optional().nullable(),
  /** Hours before a session starts that online booking closes; negative means until the session ends. */
  onlineBookingCutoffHours: z.number().optional().nullable(),
  partySize: z.number().optional().nullable(),
  paymentMethods: z.array(z.nativeEnum(PaymentMethods)).optional().nullable(),
  position: PositionSchema.optional().nullable(),
  shape: ShapeSchema.optional().nullable(),
  taxes: z.array(TaxSchemaV3),
  title: z.string().optional().nullable(),
  number: z.string().optional().nullable(),
  /** Pool-backed groups only: the unit this resource sells. Stable across groups sharing the pool. */
  resourcePoolUnitId: z.string().uuid().optional().nullable(),
  integrationMeta: z.array(IntegrationMetaSchema).optional().nullable(),
  shouldValidateComplimentaryBooking: ShouldValidateComplimentaryBookingSchema.optional().nullable(),
});

export const ResourceGroupWithMediasSchemaV3 = ResourceGroupBaseSchemaV3.extend({
  medias: z.array(MediasSchemaV3),
});

export const ResourceGroupWithExperiencePaymentMethodsSchemaV3 = ResourceGroupWithMediasSchemaV3.extend({
  experiences: z.array(
    z.object({
      id: z.string().uuid(),
      integrationMeta: z.array(IntegrationMetaSchema).optional().nullable(),
      paymentMethods: z.array(z.nativeEnum(PaymentMethods)).optional().nullable(),
      shouldValidateComplimentaryBooking: ShouldValidateComplimentaryBookingSchema.optional().nullable(),
    }),
  ),
});

export const ResourceGroupSchemaV3 = ResourceGroupWithMediasSchemaV3.extend({
  availabilityRule: AvailabilityRuleSchema.nullable().optional(),
  durations: z
    .array(z.object({ durationMinutes: z.number().int().min(1), id: z.string().uuid(), label: z.string() }))
    .optional(),
  defaultShape: ShapeSchema.optional().nullable(),
  experiences: z.array(ResourceGroupExperienceSchemaV3),
  included: z.array(z.string()),
  numberPattern: z.nativeEnum(NumberPattern),
  order: z.number().optional().nullable(),
  /** Set when the group's resources sell units of a resource pool, possibly shared with other groups. */
  resourcePoolId: z.string().uuid().optional().nullable(),
});

export type ResourceGroupV3 = z.infer<typeof ResourceGroupSchemaV3>;

export const VibesSchemaV3 = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  name: z.string(),
});
